/**
 * CallHistoryLineItem.tsx
 * Talkroute
 *
 * Created by Vel Genov on 12/26/18
 * Copyright (c) 2018 Talkroute. All right reserved.
 */

import React, { FC, memo, useState } from "react";
import { domClasses } from "@constants/testConstants";
import { makeCall } from "@actions/callActions";
import { CallHistoryData } from "@services/dataClasses/CallHistoryData";
import CallTypeIcon from "./CallTypeIcon";
import { Grid } from "@material-ui/core";
import { useDispatch } from "react-redux";

const hoverOnStyle = { opacity: "0.6" };
const hoverOffStyle = { opacity: "1.0" };
const selectedStyle = { backgroundColor: "var(--color-light-grey)" };
const textColorBlack = { color: "var(--color-charcoal)" };

type IProps = {
  call: CallHistoryData;
  selected: boolean;
  showDetailsCallback: (call: CallHistoryData) => any;
};

/**
 * A comoponent that displays a single call history line item with the passed in props.
 * This will usually be displayead as a part of a collection
 * @param {*} showDetailsCallback a callback function to show details for this call
 * @param {*} call the call to display
 */
const CallHistoryLineItem: FC<IProps> = ({ call, selected, showDetailsCallback }) => {
  const [hover, setHover] = useState(false);
  const [hoverOnCall, setHoverOnCall] = useState(false);
  const dispatch = useDispatch();

  /**
   * Called when the user is hovering over a CallHistoryLineItem
   */
  const hoverOn = () => setHover(true);

  /**
   * Called when the user is moving out of a CallHistoryLineItem
   */
  const hoverOff = () => setHover(false);

  /**
   * Called when the user is hovirng over the call icon
   */
  const hoverOnCallHandler = () => setHoverOnCall(true);

  /**
   * Called when the user is moving away from the call icon
   */
  const hoverOffCallHandler = () => setHoverOnCall(false);

  /**
   * Send an action to initiate a call
   *
   * @param numberToCall the phone number to call
   */
  const sendCallAction = () => {
    const numberToCall = call.getCallerPhoneNumber();
    dispatch(makeCall(numberToCall));
  };

  const { type, id, callResult } = call;
  const date = call.getFormattedDate();

  const textColor = textColorBlack;

  // Determine the rendering style based on hover status
  const hoverStyle = hover ? hoverOnStyle : hoverOffStyle;
  // If this call is selected, the selected style will take
  // precedence over the hover style
  const gridStyle = selected ? selectedStyle : hoverStyle;

  const callerPhoneNumber = call.getContactInfo();

  return (
    <Grid
      container
      onMouseEnter={() => hoverOn()}
      onMouseLeave={() => hoverOff()}
      style={{
        cursor: "pointer",
        borderBottom: "1px solid var(--color-dark-grey)",
        backgroundColor: "var(--color-white)",
        height: 62,
      }}
    >
      <Grid
        xs={2}
        item
        style={{
          ...gridStyle,
          ...textColorBlack,
        }}
        onMouseEnter={() => hoverOnCallHandler()}
        onMouseLeave={() => hoverOffCallHandler()}
        onClick={() => sendCallAction()}
      >
        <span
          style={{
            display: "flex",
            alignItems: "center",
            height: "100%",
            paddingLeft: 1,
          }}
        >
          {CallTypeIcon({
            type,
            callResult,
            hovering: hoverOnCall,
            clickable: true,
          })}
        </span>
      </Grid>
      <Grid
        xs={6}
        item
        onClick={() => showDetailsCallback(call)}
        style={{
          ...gridStyle,
          ...textColorBlack,
          overflow: "hidden",
          paddingLeft: 3,
        }}
        className={`text-normal ${domClasses.CALL_HISTORY_ITEM_CALLER_PHONE_NUMBER}`}
      >
        <span
          style={{ display: "flex", alignItems: "center", height: "100%" }}
        >
          {callerPhoneNumber}
        </span>
      </Grid>
      <Grid
        xs={4}
        item
        onClick={() => showDetailsCallback(call)}
        style={{
          ...gridStyle,
          ...textColor,
        }}
        className="text-small"
      >
        <span
          style={{
            display: "flex",
            alignItems: "center",
            height: "100%",
            paddingRight: 14,
            flexDirection: "row-reverse",
          }}
        >
          {date}
        </span>
      </Grid>
    </Grid>
  );
};

export default memo(CallHistoryLineItem);